import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { RecaptchaService } from '../services/recaptcha.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-recaptcha',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="recaptcha-container" *ngIf="enabled">
      <div
        #recaptchaElement
        [id]="elementId"
        class="recaptcha-widget"
        [class.recaptcha-error]="hasError"
      ></div>
      <div class="recaptcha-error-message" *ngIf="hasError && errorMessage">
        {{ errorMessage }}
      </div>
      <div class="recaptcha-loading" *ngIf="loading">
        <div class="loading-spinner"></div>
        <span>Loading security verification...</span>
      </div>
    </div>
  `,
  styles: [
    `
      .recaptcha-container {
        margin: 1rem 0;
      }

      .recaptcha-widget {
        display: inline-block;
        transition: all 0.3s ease;
        border-radius: 8px;
        overflow: hidden;
      }

      .recaptcha-widget.recaptcha-error {
        border: 2px solid #ef4444;
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
      }

      .recaptcha-error-message {
        color: #ef4444;
        font-size: 12px;
        margin-top: 6px;
        font-weight: 500;
      }

      .recaptcha-loading {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #6b7280;
        font-size: 14px;
        padding: 1rem;
        background-color: #f9fafb;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
      }

      .loading-spinner {
        width: 16px;
        height: 16px;
        border: 2px solid #e5e7eb;
        border-top: 2px solid #3859e0;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      @media (max-width: 480px) {
        .recaptcha-widget {
          transform: scale(0.85);
          transform-origin: 0 0;
        }
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RecaptchaComponent),
      multi: true,
    },
  ],
})
export class RecaptchaComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @ViewChild('recaptchaElement', { static: false })
  recaptchaElement!: ElementRef;
  @Input() errorMessage: string = 'Please complete the security verification';
  @Output() tokenChange = new EventEmitter<string>();
  @Output() error = new EventEmitter<string>();

  enabled = false;
  loading = true;
  hasError = false;
  elementId = 'recaptcha-' + Math.random().toString(36).substr(2, 9);
  private widgetId: number | null = null;
  private siteKey = '';
  private destroy$ = new Subject<void>();

  private onChange = (value: string) => {};
  private onTouched = () => {};

  constructor(
    private recaptchaService: RecaptchaService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.initializeRecaptcha();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanup();
  }

  private async initializeRecaptcha(): Promise<void> {
    try {
      this.recaptchaService
        .isEnabled()
        .pipe(takeUntil(this.destroy$))
        .subscribe((enabled) => {
          this.enabled = enabled;
          if (enabled) {
            this.loadRecaptcha();
          } else {
            this.loading = false;
          }
        });
    } catch (error) {
      console.error('Failed to initialize reCAPTCHA:', error);
      this.loading = false;
      this.enabled = false;
    }
  }

  private async loadRecaptcha(): Promise<void> {
    try {
      this.recaptchaService
        .getSiteKey()
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (siteKey) => {
          if (siteKey) {
            this.siteKey = siteKey;
            setTimeout(() => {
              this.renderRecaptcha();
            }, 100);
          } else {
            this.handleError('No reCAPTCHA site key available');
          }
        });
    } catch (error) {
      console.error('Failed to load reCAPTCHA:', error);
      this.handleError('Failed to load security verification');
    }
  }

  private async renderRecaptcha(): Promise<void> {
    try {
      this.widgetId = await this.recaptchaService.render(
        this.elementId,
        this.siteKey,
        (token: string) => this.onTokenReceived(token)
      );

      this.loading = false;
      this.hasError = false;
    } catch (error) {
      console.error('Failed to render reCAPTCHA:', error);
      this.handleError('Failed to render security verification');
    }
  }

  private onTokenReceived(token: string): void {
    this.ngZone.run(() => {
      if (token) {
        this.hasError = false;
        this.onChange(token);
        this.tokenChange.emit(token);
      } else {
        this.hasError = true;
        this.onChange('');
        this.tokenChange.emit('');
        this.error.emit('reCAPTCHA verification failed');
      }
      this.onTouched();
    });
  }

  private handleError(message: string): void {
    this.loading = false;
    this.hasError = true;
    this.error.emit(message);
    console.error('reCAPTCHA error:', message);
  }

  private cleanup(): void {
    if (this.widgetId !== null) {
      try {
        this.recaptchaService.reset(this.widgetId);
      } catch (error) {
        console.warn('Error cleaning up reCAPTCHA:', error);
      }
    }
  }

  reset(): void {
    if (this.widgetId !== null) {
      this.recaptchaService.reset(this.widgetId);
      this.hasError = false;
      this.onChange('');
    }
  }

  getToken(): string {
    if (this.widgetId !== null) {
      return this.recaptchaService.getResponse(this.widgetId);
    }
    return '';
  }

  writeValue(value: any): void {}

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {}
}
